import React, { useState } from 'react'
import { 
  XMarkIcon, 
  ExclamationTriangleIcon,
  XCircleIcon
} from '@heroicons/react/24/outline'

interface User {
  id: number
  email: string
  fullName: string
  employeeNumber?: string
  isActive: boolean
}

interface DeactivateUserModalProps {
  isOpen: boolean
  onClose: () => void
  user: User | null
  onConfirm: (userId: number, reason?: string) => Promise<void>
  isLoading?: boolean
}

const DeactivateUserModal: React.FC<DeactivateUserModalProps> = ({
  isOpen,
  onClose,
  user,
  onConfirm,
  isLoading = false
}) => {
  const [reason, setReason] = useState('')

  if (!isOpen || !user) return null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onConfirm(user.id, reason)
    setReason('')
    onClose()
  }

  const handleClose = () => {
    setReason('')
    onClose()
  }

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <XCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">
                  Deactivate User Account
                </h3>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600"
              disabled={isLoading}
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="mb-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
              <div className="flex">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mr-2" />
                <div className="text-sm text-yellow-700">
                  <p className="font-medium">Warning: This action will:</p>
                  <ul className="mt-1 list-disc list-inside space-y-1">
                    <li>Prevent the user from logging in</li>
                    <li>Revoke all temporary credentials</li>
                    <li>Maintain all historical data</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700">User Details</label>
                <div className="mt-1 bg-gray-50 rounded-md p-3">
                  <p className="text-sm">
                    <span className="font-medium">Name:</span> {user.fullName}
                  </p>
                  <p className="text-sm">
                    <span className="font-medium">Email:</span> {user.email}
                  </p>
                  {user.employeeNumber && (
                    <p className="text-sm">
                      <span className="font-medium">Employee #:</span> {user.employeeNumber}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                  Reason for Deactivation (Optional)
                </label>
                <textarea
                  id="reason"
                  name="reason"
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-red-500 focus:border-red-500 sm:text-sm"
                  placeholder="Enter reason for deactivating this account..."
                  disabled={isLoading}
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deactivating...
                </div>
              ) : (
                'Deactivate Account'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DeactivateUserModal